import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sitesApi, listClients } from '../api.js';
import type { Site, Client, HostProvider } from '../types.js';

export default function SitesList() {
  const navigate = useNavigate();

  // ── list state ──────────────────────────────────────────────
  const [sites, setSites] = useState<Site[] | null>(null);
  const [loadError, setLoadError] = useState('');

  // ── form state ──────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState('');
  // Cloudflare Pages is the house default — its free plan permits commercial use.
  const [provider, setProvider] = useState<HostProvider>('cloudflare');
  const [clients, setClients] = useState<Client[]>([]);
  // Hosts the worker holds credentials for. Absent on an older worker — treat
  // that as Vercel-only and hide the picker rather than offering a host that
  // would fail at creation time.
  const [providers, setProviders] = useState<HostProvider[]>(['vercel']);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    sitesApi.list()
      .then((r) => {
        setSites(r.sites);
        if (r.providers && r.providers.length > 0) setProviders(r.providers);
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : 'Failed to load sites'));

    listClients(1, '')
      .then((r) => setClients(r.clients))
      .catch(() => { /* clients dropdown is optional — silently degrade */ });
  }, []);

  function handleCancel() {
    setShowForm(false);
    setSlug('');
    setName('');
    setClientId('');
    setProvider('vercel');
    setFormError('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');

    const trimmedSlug = slug.trim();
    const trimmedName = name.trim();

    if (!trimmedSlug) { setFormError('Slug is required.'); return; }
    if (!trimmedName) { setFormError('Name is required.'); return; }

    setSubmitting(true);
    try {
      const res = await sitesApi.create({
        slug: trimmedSlug,
        name: trimmedName,
        ...(clientId ? { client_id: clientId } : {}),
        provider,
      });
      void navigate(`/sites/${res.site.id}`);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to create site.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) return <div className="alert alert-error">{loadError}</div>;

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Sites</h1>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + New Site
          </button>
        )}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1.25rem' }}>New Site</h2>

          {formError && (
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{formError}</div>
          )}

          <form onSubmit={(e) => { void handleSubmit(e); }}>
            <div style={{ display: 'grid', gap: '1rem', maxWidth: '480px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                <span style={{ fontSize: '.875rem', fontWeight: 500 }}>Slug <span style={{ color: '#ef4444' }}>*</span></span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="my-client-site"
                  disabled={submitting}
                  autoFocus
                />
                <span style={{ fontSize: '.78rem', color: '#64748b' }}>
                  Lowercase letters, numbers, hyphens only. Used in the host project name
                  {provider === 'cloudflare' ? ' (Cloudflare caps this at 58 chars — keep the slug under 55).' : '.'}
                </span>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                <span style={{ fontSize: '.875rem', fontWeight: 500 }}>Name <span style={{ color: '#ef4444' }}>*</span></span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Client Site"
                  disabled={submitting}
                />
              </label>

              {providers.length > 1 && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                  <span style={{ fontSize: '.875rem', fontWeight: 500 }}>Host</span>
                  <select
                    value={provider}
                    onChange={(e) => setProvider(e.target.value as HostProvider)}
                    disabled={submitting}
                  >
                    <option value="vercel">Vercel</option>
                    <option value="cloudflare">Cloudflare Pages</option>
                  </select>
                  <span style={{ fontSize: '.78rem', color: '#64748b' }}>
                    A site cannot be moved between hosts after it is created.
                  </span>
                </label>
              )}

              <label style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                <span style={{ fontSize: '.875rem', fontWeight: 500 }}>Client</span>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  disabled={submitting}
                >
                  <option value="">— none —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '.75rem', marginTop: '1.25rem' }}>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create Site'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={submitting}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        {!showForm && (
          <p style={{ color: '#475569', fontSize: '.9rem', marginBottom: '1rem' }}>
            <em>Sites are created and updated by Claude via MCP, or using the "+ New Site" button above.</em>
          </p>
        )}

        {sites === null ? (
          <p style={{ color: '#94a3b8' }}>Loading…</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>URL</th>
                <th>Host</th>
                <th>Client</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sites.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ color: '#94a3b8', padding: '1rem 0' }}>No sites found.</td>
                </tr>
              )}
              {sites.map((s) => (
                <tr key={s.id}>
                  <td><Link to={`/sites/${s.id}`}>{s.name}</Link></td>
                  <td><code style={{ fontSize: '.8rem' }}>{s.slug}</code></td>
                  <td>
                    {s.branded_url ? (
                      <a href={s.branded_url} target="_blank" rel="noreferrer" style={{ fontFamily: 'monospace', fontSize: '.85rem' }}>
                        {s.branded_url.replace('https://', '')}
                      </a>
                    ) : (
                      <a href={s.host_default_url} target="_blank" rel="noreferrer" style={{ fontFamily: 'monospace', fontSize: '.85rem' }}>
                        {s.host_default_url.replace('https://', '')}
                      </a>
                    )}
                  </td>
                  <td>{s.provider === 'cloudflare' ? 'Cloudflare' : 'Vercel'}</td>
                  <td>{s.client_id ?? '—'}</td>
                  <td>{s.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
